import { axiosInstance } from 'v3/api/core/apiConfig';

type SimulationResult = {
  nMonths: number;
  totalCost: number;
  mTotalCost: number;
  costOfCredit: number;
  mCostOfCredit: number;
  totalSavings: number;
  mTotalSavings: number;
  monthlyPayment: number;
  mMonthlyPayment: number;
  eApr: number;
  apr: number;
  downPayment: number;
  savingsList: Array<{
    costOfCredit: number;
    mCostOfCredit: number;
  }>;
};

type SimulationProps = {
  amount: number;
  terms: number;
  interval?: number;
  creditScore: number;
  downPayment: number;
};

type DownPaymentItem = {
  userChanceRange: number[];
  newMonthlyPayment: number;
};

type DownPaymentProps = Omit<SimulationProps, 'interval' | 'downPayment'>;

export type DownPaymentResult = Record<string, DownPaymentItem>;

export const DealsClient = () => {
  async function getDealsInfo(
    price: number,
    months: number,
    creditScore: number,
    downPayment: number,
    monthlyPayment?: number
  ) {
    try {
      const res = await axiosInstance.get(`/clubs/deals/`, {
        params: {
          amount: price,
          n_months: months,
          credit_score: creditScore,
          down_payment: downPayment,
          ...(monthlyPayment && { monthly_payment: monthlyPayment }),
        },
      });
      const data = res.data.result;
      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function dealClubMatch(amount: number) {
    try {
      const res = await axiosInstance.get(
        `/v2/contract-flow/match-deal/?amount=${amount}`
      );
      const data = res.data.data;
      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function getDealRanges(
    creditScore: number,
    price: number,
    terms: number,
    downPayment = 0
  ) {
    // creditScore: number, price: number, terms: number, halfPayment?: number | boolean
    try {
      const [resHalf, resFull] = await Promise.all([
        axiosInstance.get(
          `/v2/contract-flow/deal-chances/?credit_score=${creditScore}&vehicle_price=${price}&loan_term=${terms}&down_payment=${downPayment}&half_payment=1`
        ),
        axiosInstance.get(
          `/v2/contract-flow/deal-chances/?credit_score=${creditScore}&vehicle_price=${price}&loan_term=${terms}&down_payment=${downPayment}`
        ),
      ]);
      const dataHalf = resHalf.data.userChanceRange;
      const dataFull = resFull.data.userChanceRange;
      const data = { dataHalf, dataFull };
      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function simulateDeal({
    amount,
    terms,
    creditScore,
    downPayment,
    interval = 6,
  }: SimulationProps) {
    try {
      const res = await axiosInstance.get<{ result: SimulationResult }>(
        `/clubs/simulator/?amount=${amount}&n_months=${terms}&credit_score=${creditScore}&factor=${
          terms / interval
        }&down_payment=${downPayment}`
      );
      const data = res.data.result;
      return { data };
    } catch (error) {
      return { error };
    }
  }

  async function getDownPaymentInfo({
    amount,
    terms,
    creditScore,
  }: DownPaymentProps) {
    try {
      const res = await axiosInstance.get<DownPaymentResult>(
        `/v2/contract-flow/improve-deal-chances/?vehicle_price=${amount}&loan_term=${terms}&credit_score=${creditScore}`
      );
      const data = res.data;
      return { data };
    } catch (error) {
      return { error };
    }
  }

  return {
    getDealsInfo,
    dealClubMatch,
    getDealRanges,
    simulateDeal,
    getDownPaymentInfo,
  };
};
